import { cn } from "@tcg/ui";
import { useTranslation } from "react-i18next";

import { MoneyText, useYen } from "@/components/MoneyText";
import { NumPad } from "@/components/NumPad";
import type { TenderPreview } from "@/lib/types";

import type { TenderPart } from "./useTender";

/**
 * 現金 (design/05 §3.2): big number pad for お預かり, shortcut buttons (exact / next ¥1,000 /
 * ¥5,000 / ¥10,000 — the list comes from the server), お釣り from the server's preview.
 */
export function CashPad({
  part,
  preview,
  index,
  onTendered,
}: {
  part: TenderPart;
  preview: TenderPreview | undefined;
  index: number;
  onTendered: (digits: string) => void;
}) {
  const { t } = useTranslation();
  const yen = useYen();
  const line = preview?.tenders.find((l) => l.index === index);
  const suggestions = preview?.cashSuggestions ?? [];
  const shortBy = line?.shortBy ?? 0;
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_16rem]">
      <NumPad
        label={t("tender.tendered")}
        value={part.tendered}
        onChange={onTendered}
        money
        maxLength={9}
      />
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-muted-foreground">{t("tender.tendered")}</p>
        {suggestions.map((value, i) => (
          <button
            key={value}
            type="button"
            aria-pressed={(part.tendered || part.amount) === String(value)}
            onClick={() => onTendered(i === 0 ? "" : String(value))}
            className={cn(
              "min-h-pos-touch rounded-md border text-xl font-semibold tabular-nums aria-pressed:border-primary aria-pressed:bg-accent",
            )}
          >
            {i === 0 ? (
              <>
                {t("tender.exact")} <MoneyText amount={value} />
              </>
            ) : (
              <MoneyText amount={value} />
            )}
          </button>
        ))}
        <div className="mt-2 rounded-md border p-3" aria-live="polite">
          {shortBy > 0 ? (
            <p className="text-pos-problem" role="alert">
              <span className="block text-sm">{t("tender.not_enough")}</span>
              <span className="text-2xl font-semibold tabular-nums">
                {t("tender.remaining", { amount: yen(shortBy) })}
              </span>
            </p>
          ) : (
            <p>
              <span className="block text-sm text-muted-foreground">{t("tender.change")}</span>
              <MoneyText amount={line?.change ?? 0} size="xl" data-testid="change" />
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
